import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { usePageTitle } from "@/lib/seo";
import { Card, Button, Pill } from "@/components/ui/Primitives";
import { Zap, MessageSquareWarning, Wrench, Building2, CreditCard, ArrowLeft } from "lucide-react";

const ACTION_CONFIGS = [
  {
    key: "auto_dunning",
    icon: MessageSquareWarning,
    label: "Late-rent reminders",
    blurb: "Send a courtesy reminder email to tenants when rent is N days late.",
    threshold: { key: "trigger_days_late", label: "Trigger after (days late)", min: 1, max: 30, default: 3 },
  },
  {
    key: "auto_vendor_booking",
    icon: Wrench,
    label: "Vendor booking",
    blurb: "Assign a saved vendor when a maintenance ticket has been open for N hours with no assignee.",
    threshold: { key: "trigger_ticket_hours_open", label: "Trigger after (hours open)", min: 4, max: 168, default: 48 },
  },
  {
    key: "auto_listing_syndication",
    icon: Building2,
    label: "Listing syndication",
    blurb: "Publish a vacant unit listing after it's been empty for N days.",
    threshold: { key: "trigger_days_vacant", label: "Trigger after (days vacant)", min: 1, max: 60, default: 7 },
  },
  {
    key: "auto_stripe_scheduling",
    icon: CreditCard,
    label: "Rent reminder scheduling",
    blurb: "Email tenants their card-payment link N days before rent is due, so they can pay early.",
    threshold: { key: "advance_days", label: "Days before due date", min: 1, max: 14, default: 3 },
  },
];

const MODE_LABELS = {
  off: { label: "Off", tone: "slate", desc: "Won't run" },
  ask: { label: "Ask me", tone: "amber", desc: "Queues for one-tap approval" },
  auto: { label: "Auto", tone: "emerald", desc: "Runs on its own · reversible 24h" },
};

export default function AgentSettings() {
  usePageTitle("Operator settings — Central Rentals");
  const [prefs, setPrefs] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/agent/preferences")
      .then((r) => setPrefs(r.data?.prefs || {}))
      .catch(() => setPrefs({}));
  }, []);

  const update = (key, patch) => {
    setPrefs((prev) => ({ ...prev, [key]: { ...prev?.[key], ...patch } }));
  };

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put("/agent/preferences", { prefs });
      setPrefs(r.data?.prefs || prefs);
      toast.success("Operator settings saved.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    } finally { setSaving(false); }
  };

  if (!prefs) return <div className="p-6 text-sm text-slate-500">Loading…</div>;

  return (
    <div className="space-y-6 max-w-3xl" data-testid="agent-settings-page">
      <div>
        <Link to="/activity" data-testid="back-to-activity"
          className="inline-flex items-center gap-1 text-xs font-semibold text-slate-500 hover:text-slate-900 mb-3">
          <ArrowLeft className="w-3.5 h-3.5" /> Back to activity
        </Link>
        <div className="flex items-center gap-2 text-xs uppercase tracking-[0.18em] font-bold text-emerald-700 mb-1">
          <Zap className="w-3.5 h-3.5" /> Smart Operator
        </div>
        <h1 className="font-display text-2xl md:text-3xl font-bold text-slate-900">Operator settings</h1>
        <p className="text-sm text-slate-600 mt-1 max-w-xl">
          Choose which actions the Operator can take on your behalf. Everything is off by default,
          fully reversible for 24 hours, and logged in the activity ledger.
        </p>
      </div>

      <div className="space-y-3">
        {ACTION_CONFIGS.map((cfg) => {
          const cur = prefs[cfg.key] || { mode: "off" };
          const Icon = cfg.icon;
          return (
            <Card key={cfg.key} className="p-5" data-testid={`agent-action-${cfg.key}`}>
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center text-slate-700">
                  <Icon className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div>
                      <h3 className="font-display text-lg font-bold text-slate-900">{cfg.label}</h3>
                      <p className="text-sm text-slate-600 mt-0.5">{cfg.blurb}</p>
                    </div>
                    <Pill tone={MODE_LABELS[cur.mode]?.tone || "slate"}>
                      {MODE_LABELS[cur.mode]?.label || cur.mode}
                    </Pill>
                  </div>

                  <div className="mt-4 grid grid-cols-3 gap-2" data-testid={`mode-group-${cfg.key}`}>
                    {["off", "ask", "auto"].map((m) => (
                      <button key={m}
                        onClick={() => update(cfg.key, { mode: m })}
                        data-testid={`mode-${cfg.key}-${m}`}
                        className={`px-3 py-2 rounded-md text-xs font-semibold border text-left transition-colors ${
                          cur.mode === m
                            ? "border-emerald-500 bg-emerald-50 text-emerald-900"
                            : "border-slate-200 text-slate-600 hover:border-slate-300"
                        }`}>
                        <div className="text-sm font-bold">{MODE_LABELS[m].label}</div>
                        <div className="text-[11px] font-normal opacity-80">{MODE_LABELS[m].desc}</div>
                      </button>
                    ))}
                  </div>

                  {cur.mode !== "off" && cfg.threshold && (
                    <div className="mt-3 flex items-center gap-3 flex-wrap">
                      <label className="text-xs text-slate-500">{cfg.threshold.label}:</label>
                      <input
                        type="number"
                        min={cfg.threshold.min}
                        max={cfg.threshold.max}
                        value={cur[cfg.threshold.key] ?? cfg.threshold.default}
                        onChange={(e) => update(cfg.key, { [cfg.threshold.key]: Number(e.target.value) })}
                        data-testid={`threshold-${cfg.key}`}
                        className="w-24 px-2 py-1 rounded-md border border-slate-200 text-sm bg-white"
                      />
                    </div>
                  )}
                </div>
              </div>
            </Card>
          );
        })}
      </div>

      <div className="flex items-center justify-end gap-2 sticky bottom-4 bg-white/80 backdrop-blur p-3 rounded-lg border border-slate-200 shadow-sm">
        <Button onClick={save} disabled={saving} data-testid="agent-settings-save">
          {saving ? "Saving…" : "Save Operator settings"}
        </Button>
      </div>
    </div>
  );
}
